/**
 * FeedbackPage component.
 *
 * Provides a form for users to submit feedback about the Business Card
 * application. Supports both authenticated and guest users. The form sends
 * feedback via email using the backend email API.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { sendFeedback } from '../../services/feedbackService';
import { COMPANY_INFO } from '../../configs/company';

const FEEDBACK_CATEGORIES = [
  'Bug Report',
  'Feature Request',
  'General Feedback',
  'Usability Issue',
  'Other'
];

export default function FeedbackPage() {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'General Feedback',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Auto-fill name and email if user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData((prev) => ({
        ...prev,
        name: user.full_name || user.username || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [isAuthenticated, user]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      const username = user?.username || user?.email || null;
      await sendFeedback(formData, username);

      setSubmitSuccess(true);
      setFormData({
        name: user?.full_name || user?.username || '',
        email: user?.email || '',
        category: 'General Feedback',
        message: ''
      });

      // Reset success message after 5 seconds
      setTimeout(() => setSubmitSuccess(false), 5000);
    } catch (error) {
      setErrors({
        submit:
          error.message ||
          `Failed to submit feedback. Please try again or contact us `
          + `directly at ${COMPANY_INFO.contact.email.feedback}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1
            className="text-3xl md:text-4xl font-bold text-white mb-4"
          >
            Share Your Feedback
          </h1>
          <p className="text-gray-300 text-lg">
            Help us improve by sharing your thoughts, reporting bugs, or
            suggesting features.
          </p>
        </div>

        {submitSuccess && (
          <div
            className="bg-green-500/20 border border-green-500 rounded-2xl px-6 py-4 mb-6 text-center"
          >
            <p className="text-green-300 font-medium">
              Thank you for your feedback! We will respond within 5 working
              days.
            </p>
          </div>
        )}

        {errors.submit && (
          <div
            className="bg-red-500/20 border border-red-500 rounded-2xl px-6 py-4 mb-6"
          >
            <p className="text-red-300 text-sm">{errors.submit}</p>
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="space-y-5 bg-gray-800/30 backdrop-blur rounded-3xl px-8 py-8 border border-gray-700"
        >
          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Your Name <span className="text-red-400">*</span>
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(event) =>
                handleInputChange('name', event.target.value)
              }
              className={`w-full bg-gray-800/50 backdrop-blur text-white placeholder-gray-500 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 ${
                errors.name
                  ? 'border-2 border-red-500 focus:ring-red-500'
                  : 'border border-gray-700 focus:ring-violet-500'
              }`}
              placeholder="Enter your name"
            />
            {errors.name && (
              <p className="text-red-400 text-xs mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Your Email <span className="text-red-400">*</span>
            </label>
            <input
              id="email"
              type="email"
              value={formData.email}
              onChange={(event) =>
                handleInputChange('email', event.target.value)
              }
              className={`w-full bg-gray-800/50 backdrop-blur text-white placeholder-gray-500 rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 ${
                errors.email
                  ? 'border-2 border-red-500 focus:ring-red-500'
                  : 'border border-gray-700 focus:ring-violet-500'
              }`}
              placeholder="your.email@example.com"
            />
            {errors.email && (
              <p className="text-red-400 text-xs mt-1">{errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="category"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Feedback Category <span className="text-red-400">*</span>
            </label>
            <select
              id="category"
              value={formData.category}
              onChange={(event) =>
                handleInputChange('category', event.target.value)
              }
              className="w-full bg-gray-800/50 backdrop-blur text-white rounded-2xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-violet-500 border border-gray-700"
            >
              {FEEDBACK_CATEGORIES.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              htmlFor="message"
              className="block text-sm font-medium text-gray-300 mb-2"
            >
              Your Message <span className="text-red-400">*</span>
            </label>
            <textarea
              id="message"
              value={formData.message}
              onChange={(event) =>
                handleInputChange('message', event.target.value)
              }
              rows={6}
              className={`w-full bg-gray-800/50 backdrop-blur text-white placeholder-gray-500 rounded-2xl px-4 py-3 resize-vertical focus:outline-none focus:ring-2 ${
                errors.message
                  ? 'border-2 border-red-500 focus:ring-red-500'
                  : 'border border-gray-700 focus:ring-violet-500'
              }`}
              placeholder="Tell us what's on your mind..."
            />
            {errors.message && (
              <p className="text-red-400 text-xs mt-1">{errors.message}</p>
            )}
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full px-6 py-3 rounded-2xl font-semibold text-base transition-all duration-300 shadow-lg ${
                isSubmitting
                  ? 'bg-gray-700/50 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white hover:shadow-xl hover:scale-[1.02]'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
